/**
 * Debounced single-parameter validation against the backend
 * `/validate-param` endpoint. Returns either null (valid) or the localized
 * error message; callers wire this into the form's onBlur handler.
 *
 * In-flight requests are tied to per-field AbortControllers so a rapid
 * re-validate or an unmount cancels the previous fetch before its setState
 * would land on a stale (possibly unmounted) component.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { anomalyApi } from './anomalyApi';
import { log } from '@/core/debug/anomalyLogger';
import { AnomalyParameters, Lang, paramsToRequest } from './types';

const DEBOUNCE_MS = 300;

export function useParameterValidation(
  params: AnomalyParameters,
  lang: Lang,
  uploadId: string | null,
  opts?: { onShapeError?: (err: Error) => void },
) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const controllers = useRef<Record<string, AbortController>>({});
  const onShapeErrorRef = useRef(opts?.onShapeError);
  useEffect(() => {
    onShapeErrorRef.current = opts?.onShapeError;
  }, [opts?.onShapeError]);

  useEffect(() => {
    const t = timers.current;
    const c = controllers.current;
    return () => {
      Object.values(t).forEach(clearTimeout);
      Object.values(c).forEach((ctrl) => ctrl.abort());
    };
  }, []);

  const validate = useCallback(
    (name: string, value: unknown) => {
      if (timers.current[name]) clearTimeout(timers.current[name]);
      controllers.current[name]?.abort();

      // null = "not set" — clear any stale error and skip backend round-trip.
      // Mirrors the backend `validate_par_dict` policy: empty input is a
      // legal in-progress edit. Required-field and cross-field constraints
      // (SBAD pair, STL/LSTM enabled-without-period, V_MAX vs V_MIN) are
      // enforced at submit time by /start.
      if (value === null) {
        setErrors((prev) => {
          if (!(name in prev)) return prev;
          const next = { ...prev };
          delete next[name];
          return next;
        });
        return;
      }

      timers.current[name] = setTimeout(async () => {
        const ctrl = new AbortController();
        controllers.current[name] = ctrl;
        try {
          const res = await anomalyApi.validateParam(
            name,
            value,
            paramsToRequest(params),
            lang,
            uploadId ?? undefined,
            { signal: ctrl.signal },
          );
          if (ctrl.signal.aborted) return;
          setErrors((prev) => {
            const next = { ...prev };
            if (res.ok) delete next[name];
            else next[name] = res.error ?? 'Invalid';
            return next;
          });
        } catch (err) {
          if ((err as Error)?.name === 'AbortError') return;
          // Non-Abort errors (network failure, ShapeError from validator drift,
          // etc.) are logged for forensics but not surfaced in `errors` — the
          // form-level Submit will re-validate via /start and surface hard
          // errors there. Silent at the UI; visible in the logger.
          log.err('useParameterValidation', err);
          if ((err as Error)?.name === 'ShapeError') {
            onShapeErrorRef.current?.(err as Error);
          }
        }
      }, DEBOUNCE_MS);
    },
    [params, lang, uploadId],
  );

  return { errors, validate };
}
